import React, { useState } from 'react';
import {
  Box,
  Chip,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DeleteOutline as DeleteOutlineIcon,
  DriveFileMove as DriveFileMoveIcon,
  Link as LinkIcon,
  MoreVert as MoreVertIcon,
  Pause as PauseIcon,
  PlayArrow as PlayArrowIcon,
  UploadFile as UploadFileIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import AnalyticsService from '../../services/AnalyticsService';
import { viewerSeesRevenue } from '../../utils/analyticsAccess';
import {
  formatDate,
  formatDuration,
  formatMoney,
  formatNumber,
  formatShare,
  rpmTier,
} from '../../utils/analyticsFormat';
import { Change, ChannelAvatar, INK, Metric, Section, Sparkline } from './AnalyticsParts';
import { ChannelSettingsDialog, ImportDialog, RemoveChannelDialog } from './ChannelDialogs';
import ConnectionDialog from './ConnectionDialog';
import { tooltipDate } from './DailyMetricChart';
import TagChip from './TagChip';

// Reported by YouTube, imported, or estimated from views at a set RPM.
export function hasRevenue(channel) {
  return Boolean(channel.hasRevenue);
}

function userTagChip(tag) {
  return <TagChip key={`tag-${tag}`} tag={tag} />;
}

export function UserTagChips({ tags }) {
  return tags.map(userTagChip);
}

/** The user's tags, then status and context tags, shown under a channel's name. */
export function ChannelTags({ channel, showRpm = true, sx }) {
  const tags = (channel.tags || []).map(userTagChip);
  if (channel.uploadChannelName)
    tags.push(
      <Tooltip key="upload-channel" title="Reads through this upload channel's YouTube connection.">
        <Chip size="small" variant="outlined" color="primary" label={channel.uploadChannelName} />
      </Tooltip>,
    );
  if (channel.problem)
    tags.push(
      <Tooltip key="problem" title={channel.problem}>
        <Chip size="small" color="error" variant="outlined" label="Needs attention" />
      </Tooltip>,
    );
  if (channel.status === 'paused')
    tags.push(<Chip key="paused" size="small" label="Pulls paused" />);
  if (channel.source === 'import')
    tags.push(
      <Tooltip
        key="import"
        title="Numbers come from YouTube Studio exports. Daily revenue is estimated from each day's views, so it can differ from YouTube's daily figures."
      >
        <Chip size="small" variant="outlined" label="CSV import · estimated revenue" />
      </Tooltip>,
    );
  if (channel.revenueNote === 'estimated-rpm')
    tags.push(
      <Tooltip
        key="revenue"
        title={`YouTube does not report this channel's revenue, so it is estimated from views at ${formatMoney(channel.revenueEstimate.rpm)} RPM from ${formatDate(channel.revenueEstimate.from)}. Change it in Channel settings.`}
      >
        <Chip
          size="small"
          variant="outlined"
          label={`Estimated revenue · ${formatMoney(channel.revenueEstimate.rpm)} RPM`}
        />
      </Tooltip>,
    );
  if (channel.revenueNote === 'access-missing')
    tags.push(
      <Tooltip
        key="revenue"
        title={
          channel.uploadConnectionKind
            ? "Upload connections cannot read revenue. Set an estimated RPM in Channel settings, or connect the channel with its own analytics connection."
            : 'Reconnect this channel and approve revenue access to include revenue.'
        }
      >
        <Chip size="small" color="warning" variant="outlined" label="No revenue access" />
      </Tooltip>,
    );
  if (channel.revenueNote === 'not-monetized')
    tags.push(<Chip key="revenue" size="small" variant="outlined" label="Revenue unavailable" />);
  const tier = showRpm && hasRevenue(channel) ? rpmTier(channel.rpm) : null;
  if (tier)
    tags.push(<Chip key="rpm" size="small" color={tier.color} variant="outlined" label={tier.label} />);
  if (channel.revenueShare < 1)
    tags.push(
      <Chip key="share" size="small" variant="outlined" label={`Your share ${formatShare(channel.revenueShare)}`} />,
    );
  if (channel.source === 'import' && channel.dataThrough)
    tags.push(
      <Typography key="through" variant="caption" color="text.secondary">
        Data through {formatDate(channel.dataThrough)}
      </Typography>,
    );
  if (!tags.length) return null;
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, mt: 0.5, ...sx }}>
      {tags}
    </Box>
  );
}

/** The ⋮ menu on a channel row, with its dialogs. */
export function ChannelMenu({ channel, folders, existingTags, onChanged }) {
  const [anchor, setAnchor] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState(false);

  const open = (name) => {
    setAnchor(null);
    setDialog(name);
  };
  const close = () => setDialog(null);
  const saved = () => {
    setDialog(null);
    onChanged();
  };

  async function togglePause() {
    setAnchor(null);
    setBusy(true);
    try {
      await AnalyticsService.updateChannel(channel.id, {
        status: channel.status === 'paused' ? 'active' : 'paused',
      });
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  const connected = channel.source === 'youtube';
  // A channel linked to an upload channel is connected from Channels instead.
  const ownConnection = connected && !channel.uploadConnectionKind;

  return (
    <Box onClick={(event) => event.stopPropagation()}>
      <IconButton
        aria-label={`Manage ${channel.name}`}
        onClick={(event) => setAnchor(event.currentTarget)}
        disabled={busy}
      >
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        <MenuItem onClick={() => open('settings')}>
          <ListItemIcon>
            <DriveFileMoveIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{viewerSeesRevenue() ? 'Folder, tags, and revenue share' : 'Folder and tags'}</ListItemText>
        </MenuItem>
        {ownConnection && (
          <MenuItem onClick={() => open('connection')}>
            <ListItemIcon>
              <LinkIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>YouTube connection</ListItemText>
          </MenuItem>
        )}
        {connected && channel.connected && (
          <MenuItem onClick={togglePause}>
            <ListItemIcon>
              {channel.status === 'paused' ? <PlayArrowIcon fontSize="small" /> : <PauseIcon fontSize="small" />}
            </ListItemIcon>
            <ListItemText>{channel.status === 'paused' ? 'Resume scheduled pulls' : 'Pause scheduled pulls'}</ListItemText>
          </MenuItem>
        )}
        {!connected && (
          <MenuItem onClick={() => open('import')}>
            <ListItemIcon>
              <UploadFileIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Upload new export</ListItemText>
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={() => open('remove')} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <DeleteOutlineIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Remove channel</ListItemText>
        </MenuItem>
      </Menu>
      {dialog === 'settings' && (
        <ChannelSettingsDialog
          channel={channel}
          folders={folders}
          existingTags={existingTags}
          onClose={close}
          onSaved={saved}
        />
      )}
      {dialog === 'connection' && (
        <ConnectionDialog channel={channel} onClose={saved} onChanged={onChanged} />
      )}
      {dialog === 'import' && (
        <ImportDialog channel={channel} onClose={close} onImported={onChanged} />
      )}
      {dialog === 'remove' && (
        <RemoveChannelDialog channel={channel} onClose={close} onRemoved={saved} />
      )}
    </Box>
  );
}

function ChannelIdentity({ channel }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
      <ChannelAvatar channel={channel} />
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, color: INK }} noWrap title={channel.name}>
          {channel.name}
        </Typography>
        <ChannelTags channel={channel} />
      </Box>
    </Box>
  );
}

// The channel's name spans the row on phones, with its menu beside it; on
// wider screens the menu has its own column at the end of the row.
function RowHeading({ channel, folders, existingTags, onChanged }) {
  return (
    <Box sx={{ gridColumn: { xs: '1 / -1', md: 'auto' }, display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <ChannelIdentity channel={channel} />
      </Box>
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        <ChannelMenu channel={channel} folders={folders} existingTags={existingTags} onChanged={onChanged} />
      </Box>
    </Box>
  );
}

const rowSx = {
  display: 'grid',
  alignItems: 'center',
  columnGap: 2,
  rowGap: 1.5,
  px: 2,
  py: 1.5,
  borderRadius: 2,
  cursor: 'pointer',
  boxShadow: 'none',
  transition: 'border-color 120ms, box-shadow 120ms',
  '&:hover': { borderColor: 'primary.light', boxShadow: 1 },
  '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
};

// Profit is shown under revenue once the channel has expenses in the range.
function RevenueMetric({ channel }) {
  if (!hasRevenue(channel)) return <Metric label="Revenue" value="—" />;
  const cut = channel.revenueShare < 1;
  return (
    <Metric
      label={cut ? 'Your revenue' : 'Revenue'}
      value={formatMoney(cut ? channel.revenueNet : channel.revenue)}
      change={
        <Change
          compact
          current={cut ? channel.revenueNet : channel.revenue}
          previous={cut ? channel.previous.revenueNet : channel.previous.revenue}
        />
      }
      note={[
        cut && `Total ${formatMoney(channel.revenue)}`,
        channel.expenses > 0 && `Profit ${formatMoney(channel.profit)}`,
      ]}
    />
  );
}

// Tooltip for one point of a row's trend line. Revenue follows the row's
// revenue metric, so it is the user's share when the channel is split.
function TrendPoint({ channel, index, date, bucketDays }) {
  const cut = channel.revenueShare < 1;
  // Revenue is left out of the series for viewers who can't see it.
  const revenue = hasRevenue(channel) ? channel.series.revenue[index] : null;
  return (
    <>
      <Box component="span" sx={{ display: 'block', opacity: 0.8 }}>
        {tooltipDate(date, bucketDays)}
      </Box>
      <Box component="span" sx={{ display: 'block', fontWeight: 600 }}>
        {formatNumber(channel.series.views[index])} views
      </Box>
      {revenue !== null && (
        <Box component="span" sx={{ display: 'block' }}>
          {formatMoney(cut ? revenue * channel.revenueShare : revenue)}{' '}
          {cut ? 'your revenue' : 'revenue'}
        </Box>
      )}
    </>
  );
}

/**
 * A channel's actuals for the selected range.
 * @param {string[]} props.seriesDates - first day of each trend-line point
 * @param {number} props.bucketDays - days each trend-line point covers
 */
export function ChannelRow({
  channel,
  showRevenue,
  seriesDates,
  bucketDays,
  folders,
  existingTags,
  onChanged,
}) {
  const navigate = useNavigate();
  const openDetail = () => navigate(`/analytics/channels/${channel.id}`);
  return (
    <Paper
      variant="outlined"
      role="link"
      tabIndex={0}
      onClick={openDetail}
      onKeyDown={(event) => event.key === 'Enter' && openDetail()}
      sx={{
        ...rowSx,
        gridTemplateColumns: {
          xs: 'repeat(2, minmax(0, 1fr))',
          md: `minmax(0, 2.4fr) repeat(${showRevenue ? 4 : 2}, minmax(0, 1fr)) 110px 40px`,
        },
      }}
    >
      <RowHeading channel={channel} folders={folders} existingTags={existingTags} onChanged={onChanged} />
      {showRevenue && <RevenueMetric channel={channel} />}
      <Metric
        label="Views"
        value={formatNumber(channel.views)}
        change={<Change compact current={channel.views} previous={channel.previous.views} />}
      />
      {showRevenue && (
        <Metric label="RPM" value={hasRevenue(channel) ? formatMoney(channel.rpm) : '—'} />
      )}
      <Metric
        label="Avg view duration"
        value={formatDuration(channel.averageViewDurationSeconds)}
        change={
          <Change
            compact
            current={channel.averageViewDurationSeconds}
            previous={channel.previous.averageViewDurationSeconds}
          />
        }
      />
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        <Sparkline
          values={hasRevenue(channel) ? channel.series.revenue : channel.series.views}
          label={hasRevenue(channel) ? 'Revenue trend' : 'Views trend'}
          describePoint={(index) => (
            <TrendPoint
              channel={channel}
              index={index}
              date={seriesDates[index]}
              bucketDays={bucketDays}
            />
          )}
        />
      </Box>
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        <ChannelMenu channel={channel} folders={folders} existingTags={existingTags} onChanged={onChanged} />
      </Box>
    </Paper>
  );
}

function basisNote(channel, basis) {
  if (basis === 'lifetime')
    return `Lifetime average · ${formatNumber(channel.forecastBasisDays)} days`;
  if (channel.newlyMonetized) return `${channel.forecastBasisDays}-day average · newly monetized`;
  return `${basis}-day average`;
}

function ProjectedMetric({ label, net, gross, share, note }) {
  const cut = share < 1;
  return (
    <Metric
      label={cut ? `${label} · your share` : label}
      value={`≈ ${formatMoney(net)}`}
      note={[cut ? `Total ≈ ${formatMoney(gross)}` : null, note].filter(Boolean).join(' · ') || null}
    />
  );
}

/** A channel's revenue projection on the chosen basis. */
export function ProjectedRow({ channel, basis, folders, existingTags, onChanged }) {
  const navigate = useNavigate();
  const openDetail = () => navigate(`/analytics/channels/${channel.id}`);
  return (
    <Paper
      variant="outlined"
      role="link"
      tabIndex={0}
      onClick={openDetail}
      onKeyDown={(event) => event.key === 'Enter' && openDetail()}
      sx={{
        ...rowSx,
        gridTemplateColumns: {
          xs: 'repeat(2, minmax(0, 1fr))',
          md: 'minmax(0, 2fr) repeat(2, minmax(0, 1.2fr)) 40px',
        },
      }}
    >
      <RowHeading channel={channel} folders={folders} existingTags={existingTags} onChanged={onChanged} />
      {hasRevenue(channel) ? (
        <>
          <ProjectedMetric
            label="Per month"
            net={channel.projectedMonth}
            gross={channel.projectedMonthGross}
            share={channel.revenueShare}
            note={basisNote(channel, basis)}
          />
          <ProjectedMetric
            label="Per year"
            net={channel.projectedYear}
            gross={channel.projectedYearGross}
            share={channel.revenueShare}
          />
        </>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ gridColumn: { xs: '1 / -1', md: '2 / 4' } }}>
          No revenue to project.
        </Typography>
      )}
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        <ChannelMenu channel={channel} folders={folders} existingTags={existingTags} onChanged={onChanged} />
      </Box>
    </Paper>
  );
}

/** A folder heading with its subtotal, followed by its channel rows. */
export function FolderSection({ group, subtotal, children }) {
  return (
    <Section
      title={
        <>
          {group.name}
          <Typography component="span" color="text.secondary" sx={{ ml: 1, fontSize: 14, fontWeight: 500 }}>
            {group.channelCount} {group.channelCount === 1 ? 'channel' : 'channels'}
          </Typography>
        </>
      }
      action={
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
          {subtotal}
        </Box>
      }
    >
      <Box sx={{ display: 'grid', gap: 1 }}>{children}</Box>
    </Section>
  );
}
